import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { API_URL } from '../App';

const C = {
  bg: '#0D0D12', card: '#1C1C28', border: '#2A2A3A', surface: '#16161F',
  accent: '#E8A2B8', teal: '#7ECECA', gold: '#D4AF7A',
  text: '#F0EEF5', muted: '#8A8A9E', green: '#7ECBA5', purple: '#B39DDB',
};

const SYMPTOMS = [
  { id: 'spotting',   emoji: '●', label: 'Bleeding',     short: 'Bleed',   color: '#C97A9A' },
  { id: 'cramps',     emoji: '⚡', label: 'Cramps',       short: 'Cramps',  color: '#FF6B7A' },
  { id: 'mood_low',   emoji: '🌧', label: 'Low mood',     short: 'Low mood',color: '#7ECECA' },
  { id: 'headache',   emoji: '◎', label: 'Headache',     short: 'Head',    color: '#8A8A9E' },
  { id: 'bloat',      emoji: '◉', label: 'Bloating',     short: 'Bloat',   color: '#7ECBA5' },
  { id: 'skinflare',  emoji: '◈', label: 'Skin flare',   short: 'Skin',    color: '#FF9F7A' },
  { id: 'sex',        emoji: '♥', label: 'Intimacy',     short: 'Intim.',  color: '#E8A2B8' },
  { id: 'mood_high',  emoji: '✦', label: 'High energy',  short: 'Energy',  color: '#D4AF7A' },
];

const PHASES = [
  { label: 'Mens.',     days: 5,  color: '#E8A2B8' },
  { label: 'Follic.',   days: 8,  color: '#D4AF7A' },
  { label: 'Ov.',       days: 1,  color: '#7ECECA' },
  { label: 'Luteal',    days: 14, color: '#7ECBA5' },
];

function localDateStr(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function offsetDate(base, days) {
  const [y, m, d] = base.split('-').map(Number);
  return localDateStr(new Date(y, m - 1, d + days));
}

function getCycleDay(dateStr, cycles) {
  if (!cycles || cycles.length === 0) return null;
  const date = new Date(dateStr + 'T00:00:00');
  const cycle = cycles.find(c => {
    const start = new Date(c.start_date);
    start.setHours(0, 0, 0, 0);
    return start <= date;
  });
  if (!cycle) return null;
  const start = new Date(cycle.start_date);
  start.setHours(0, 0, 0, 0);
  date.setHours(0, 0, 0, 0);
  const diff = Math.floor((date - start) / (1000 * 60 * 60 * 24)) + 1;
  if (diff < 1 || diff > 35) return null;
  return Math.min(diff, 28);
}

export default function Patterns({ user }) {
  const [logs, setLogs]       = useState([]);
  const [cycles, setCycles]   = useState([]);
  const [patterns, setPatterns] = useState(null);
  const [loading, setLoading] = useState(true);

  const today = localDateStr(new Date());

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    Promise.all([
      axios.get(`${API_URL}/api/logs`,     { headers: { Authorization: `Bearer ${token}` } }),
      axios.get(`${API_URL}/api/cycles`,   { headers: { Authorization: `Bearer ${token}` } }),
      axios.get(`${API_URL}/api/patterns`, { headers: { Authorization: `Bearer ${token}` } }),
    ]).then(([lr, cr, pr]) => {
      setLogs(lr.data);
      setCycles(cr.data);
      setPatterns(pr.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  // ── Calendar: log map by date ──────────────────────────────────────────────
  const logMap = useMemo(() => {
    const map = {};
    logs.forEach(l => {
      const d = (l.date || '').split('T')[0];
      if (!map[d]) map[d] = [];
      if (!map[d].includes(l.category)) map[d].push(l.category);
    });
    return map;
  }, [logs]);

  // ── Heatmap: symptom × cycle day ──────────────────────────────────────────
  const heatmap = useMemo(() => {
    const matrix = {};
    SYMPTOMS.forEach(s => { matrix[s.id] = new Array(29).fill(0); });
    logs.forEach(l => {
      const day = getCycleDay((l.date || '').split('T')[0], cycles);
      if (day && matrix[l.category]) matrix[l.category][day]++;
    });
    return matrix;
  }, [logs, cycles]);

  const heatmapMax = useMemo(() => {
    const m = {};
    SYMPTOMS.forEach(s => { m[s.id] = Math.max(1, ...heatmap[s.id].slice(1)); });
    return m;
  }, [heatmap]);

  // ── Frequency: count per symptom ─────────────────────────────────────────
  const frequency = useMemo(() => {
    const counts = {};
    SYMPTOMS.forEach(s => { counts[s.id] = 0; });
    logs.forEach(l => { if (counts[l.category] !== undefined) counts[l.category]++; });
    const maxCount = Math.max(1, ...Object.values(counts));
    return SYMPTOMS
      .map(s => ({ ...s, count: counts[s.id], pct: Math.round((counts[s.id] / maxCount) * 100) }))
      .filter(s => s.count > 0)
      .sort((a, b) => b.count - a.count);
  }, [logs]);

  // ── Calendar grid (5 weeks, Mon-aligned) ──────────────────────────────────
  const todayDt  = new Date(today + 'T00:00:00');
  const dow      = todayDt.getDay();
  const toMon    = dow === 0 ? 6 : dow - 1;
  const calStart = offsetDate(today, -(toMon + 28));
  const calDays  = Array.from({ length: 35 }, (_, i) => offsetDate(calStart, i));

  if (loading) {
    return <div style={{ padding: 40, textAlign: 'center', color: C.muted, fontFamily: 'monospace', fontSize: 11 }}>LOADING PATTERNS...</div>;
  }

  if (logs.length === 0) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div>
          <h2 style={{ fontFamily: "'DM Serif Display',serif", color: C.text, fontSize: 22, margin: '0 0 4px' }}>Patterns</h2>
          <p style={{ fontSize: 12, color: C.muted, margin: 0 }}>No data yet</p>
        </div>
        <div style={{ background: C.card, borderRadius: 16, padding: 24, border: `1px solid ${C.border}`, textAlign: 'center' }}>
          <div style={{ fontSize: 28, marginBottom: 12 }}>◈</div>
          <div style={{ fontSize: 14, color: C.text, fontWeight: 600, marginBottom: 8 }}>Start logging to see patterns</div>
          <div style={{ fontSize: 12, color: C.muted }}>Log daily symptoms. Patterns appear after a few entries.</div>
        </div>
      </div>
    );
  }

  const CELL = 12; // heatmap cell width px

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* Header */}
      <div>
        <h2 style={{ fontFamily: "'DM Serif Display',serif", color: C.text, fontSize: 22, margin: '0 0 4px' }}>Patterns</h2>
        <p style={{ fontSize: 12, color: C.muted, margin: 0 }}>
          {logs.length} entries · {cycles.length} cycle{cycles.length !== 1 ? 's' : ''} tracked
        </p>
      </div>

      {/* ── 1. ACTIVITY CALENDAR ─────────────────────────────────────────── */}
      <div style={{ background: C.card, borderRadius: 16, padding: 14, border: `1px solid ${C.border}` }}>
        <div style={{ fontSize: 11, color: C.muted, fontFamily: 'monospace', marginBottom: 10 }}>ACTIVITY · LAST 5 WEEKS</div>

        {/* Weekday headers */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2, marginBottom: 3 }}>
          {['M','T','W','T','F','S','S'].map((d, i) => (
            <div key={i} style={{ textAlign: 'center', fontSize: 9, color: C.muted, fontFamily: 'monospace' }}>{d}</div>
          ))}
        </div>

        {/* Day cells */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
          {calDays.map(d => {
            const isFuture = d > today;
            const isToday  = d === today;
            const cats     = logMap[d] || [];
            const dayNum   = parseInt(d.split('-')[2]);
            return (
              <div key={d} style={{
                background: isToday ? `${C.accent}22` : cats.length > 0 ? C.surface : 'transparent',
                border: `1px solid ${isToday ? C.accent : cats.length > 0 ? C.border : 'transparent'}`,
                borderRadius: 5,
                padding: '3px 2px 4px',
                minHeight: 42,
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                opacity: isFuture ? 0.15 : 1,
              }}>
                <div style={{ fontSize: 8, color: isToday ? C.accent : C.muted, fontFamily: 'monospace', marginBottom: 3 }}>{dayNum}</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, justifyContent: 'center', maxWidth: 20 }}>
                  {cats.slice(0, 6).map((cat, i) => {
                    const sym = SYMPTOMS.find(s => s.id === cat);
                    return <div key={i} style={{ width: 5, height: 5, borderRadius: '50%', background: sym?.color || C.accent }} />;
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* Dot legend */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px 10px', marginTop: 10 }}>
          {SYMPTOMS.map(s => (
            <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: s.color, flexShrink: 0 }} />
              <span style={{ fontSize: 9, color: C.muted }}>{s.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── 2. CYCLE-DAY HEATMAP ─────────────────────────────────────────── */}
      {cycles.length > 0 && (
        <div style={{ background: C.card, borderRadius: 16, padding: 14, border: `1px solid ${C.border}` }}>
          <div style={{ fontSize: 11, color: C.muted, fontFamily: 'monospace', marginBottom: 10 }}>SYMPTOM BY CYCLE DAY</div>
          <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <div style={{ minWidth: 72 + 28 * (CELL + 1) }}>

              {/* Phase band */}
              <div style={{ display: 'flex', marginLeft: 72, marginBottom: 5, gap: 1 }}>
                {PHASES.map(p => (
                  <div key={p.label} style={{
                    width: p.days * (CELL + 1) - 1,
                    background: `${p.color}22`,
                    borderTop: `2px solid ${p.color}55`,
                    borderRadius: '2px 2px 0 0',
                    paddingTop: 2,
                    overflow: 'hidden',
                  }}>
                    <div style={{ fontSize: 7, color: p.color, fontFamily: 'monospace', paddingLeft: 2, whiteSpace: 'nowrap' }}>{p.label}</div>
                  </div>
                ))}
              </div>

              {/* Day numbers (every 7) */}
              <div style={{ display: 'flex', marginLeft: 72, marginBottom: 4 }}>
                {Array.from({ length: 28 }, (_, i) => i + 1).map(d => (
                  <div key={d} style={{
                    width: CELL + 1, textAlign: 'center',
                    fontSize: 7, fontFamily: 'monospace',
                    color: [1, 7, 14, 21, 28].includes(d) ? C.muted : 'transparent',
                  }}>{d}</div>
                ))}
              </div>

              {/* Symptom rows */}
              {SYMPTOMS.map(s => {
                const row = heatmap[s.id];
                const max = heatmapMax[s.id];
                const hasAny = row.some(v => v > 0);
                return (
                  <div key={s.id} style={{ display: 'flex', alignItems: 'center', marginBottom: 3, opacity: hasAny ? 1 : 0.35 }}>
                    <div style={{ width: 72, display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0 }}>
                      <span style={{ fontSize: 12, color: s.color }}>{s.emoji}</span>
                      <span style={{ fontSize: 9, color: C.muted }}>{s.short}</span>
                    </div>
                    <div style={{ display: 'flex', gap: 1 }}>
                      {Array.from({ length: 28 }, (_, i) => i + 1).map(d => {
                        const count = row[d] || 0;
                        const intensity = count > 0 ? 0.2 + (count / max) * 0.8 : 0;
                        return (
                          <div key={d}
                            title={count > 0 ? `Day ${d}: logged ${count}×` : undefined}
                            style={{
                              width: CELL, height: 20, borderRadius: 2,
                              background: count > 0 ? s.color : C.surface,
                              opacity: count > 0 ? intensity : 1,
                              border: `1px solid ${count > 0 ? 'transparent' : C.border + '60'}`,
                            }} />
                        );
                      })}
                    </div>
                  </div>
                );
              })}

              {/* Heatmap scale */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 72, marginTop: 8 }}>
                <span style={{ fontSize: 8, color: C.muted, fontFamily: 'monospace' }}>NEVER</span>
                {[0.2, 0.45, 0.7, 0.9].map(o => (
                  <div key={o} style={{ width: 10, height: 10, borderRadius: 2, background: C.accent, opacity: o }} />
                ))}
                <span style={{ fontSize: 8, color: C.muted, fontFamily: 'monospace' }}>ALWAYS</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── 3. FREQUENCY BARS ────────────────────────────────────────────── */}
      {frequency.length > 0 && (
        <div style={{ background: C.card, borderRadius: 16, padding: 14, border: `1px solid ${C.border}` }}>
          <div style={{ fontSize: 11, color: C.muted, fontFamily: 'monospace', marginBottom: 12 }}>SYMPTOM FREQUENCY</div>
          {frequency.map(s => (
            <div key={s.id} style={{ marginBottom: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                  <span style={{ fontSize: 13, color: s.color }}>{s.emoji}</span>
                  <span style={{ fontSize: 12, color: C.text }}>{s.label}</span>
                </div>
                <span style={{ fontSize: 10, color: s.color, fontFamily: 'monospace' }}>{s.count}×</span>
              </div>
              <div style={{ background: C.surface, height: 6, borderRadius: 3, overflow: 'hidden' }}>
                <div style={{
                  width: `${s.pct}%`, height: '100%',
                  background: `linear-gradient(90deg, ${s.color}cc, ${s.color})`,
                  borderRadius: 3,
                }} />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── 4. INSIGHTS ──────────────────────────────────────────────────── */}
      {patterns?.insights?.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ fontSize: 11, color: C.muted, fontFamily: 'monospace' }}>INSIGHTS</div>
          {patterns.insights.map((insight, idx) => (
            <div key={idx} style={{ background: C.card, borderRadius: 16, padding: 16, border: `1px solid ${C.purple}44` }}>
              <div style={{ fontSize: 10, color: C.purple, fontFamily: 'monospace', marginBottom: 6 }}>◈ INSIGHT</div>
              <div style={{ fontSize: 13, color: C.text, lineHeight: 1.5 }}>{insight}</div>
            </div>
          ))}
        </div>
      )}

    </div>
  );
}
